import { useEffect, useRef, type ReactNode } from "react";
import { LOCALES, isLocale, useI18n } from "./i18n";
import type { Tone } from "./vend";

export function AppBar({ onHome }: { onHome?: () => void }) {
  const { t, locale, setLocale } = useI18n();
  return (
    <header className="aseed-appbar aseed-appbar--brand v-appbar">
      {onHome ? (
        <button type="button" className="v-appbar__home" onClick={onHome} aria-label={t("vend.home")}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M15 5l-7 7 7 7" />
          </svg>
        </button>
      ) : null}
      <span className="aseed-appbar__title v-appbar__title">{t("app.name")}</span>
      <label className="v-lang">
        <span className="aseed-visually-hidden">{t("vend.language")}</span>
        <select
          name="locale"
          value={locale}
          onChange={(e) => {
            if (isLocale(e.target.value)) setLocale(e.target.value);
          }}
        >
          {LOCALES.map((l) => (
            <option key={l.code} value={l.code}>
              {l.label}
            </option>
          ))}
        </select>
      </label>
    </header>
  );
}

const STEP_KEYS = ["vend.steps.scan", "vend.steps.select", "vend.steps.pay", "vend.steps.receive"] as const;

/** Scan > Product > Pay > Collect. `current` is the step in progress. */
export function Steps({ current, problem }: { current: number; problem?: boolean }) {
  const { t } = useI18n();
  return (
    <ol className="v-steps">
      {STEP_KEYS.map((key, i) => {
        const state = i < current ? "done" : i === current ? (problem ? "problem" : "current") : "todo";
        return (
          <li key={key} className={`v-steps__item v-steps__item--${state}`} aria-current={i === current ? "step" : undefined}>
            <span className="v-steps__dot" aria-hidden="true" />
            <span className="v-steps__label">{t(key)}</span>
          </li>
        );
      })}
    </ol>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <span className="v-spinner" role="status">
      {label ? <span className="aseed-visually-hidden">{label}</span> : null}
    </span>
  );
}

export function StatusIcon({ tone }: { tone: Tone }) {
  if (tone === "waiting" || tone === "progress") return <Spinner />;
  const path =
    tone === "success"
      ? "M5 12.5l4.5 4.5L19 7.5"
      : tone === "problem"
        ? "M12 7v6M12 16.5v.5"
        : tone === "action"
          ? "M6 12h12M13 6l6 6-6 6"
          : "M7 12h10";
  return (
    <span className={`v-status-icon v-status-icon--${tone}`} aria-hidden="true">
      <svg viewBox="0 0 24 24">
        <path d={path} />
      </svg>
    </span>
  );
}

export function ErrorCard({ children }: { children: ReactNode }) {
  // Shown at the top of the screen; bring it into view even when the customer
  // is scrolled down at the control that caused it.
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
  }, [children]);
  return (
    <div ref={ref} className="aseed-status-banner aseed-status-banner--error v-error" role="alert">
      {children}
    </div>
  );
}

/** A modal dialog on <dialog>, so focus and Escape behave natively. */
export function Modal({
  open,
  title,
  children,
  onClose,
}: {
  open: boolean;
  title: string;
  children: ReactNode;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);
  return (
    <dialog ref={ref} className="v-modal" onClose={onClose} aria-labelledby="v-modal-title">
      <h2 id="v-modal-title" className="v-modal__title">
        {title}
      </h2>
      {children}
    </dialog>
  );
}
